import { lazy, Suspense } from 'react';
import { cn } from '@/lib/utils';
import { textareaClass } from './styles';
import type { CodeEditorProps } from './CodeEditor';

// Lazy boundary for the CodeMirror editor. The heavy CodeEditor chunk loads
// only when this renders with highlight=true; until then (and as the
// Suspense fallback) we show the same plain <textarea> the form used before.
// When highlight=false the import is never triggered at all — that's the
// Measurement "syntax highlighting off" path.
const CodeEditor = lazy(() => import('./CodeEditor'));

interface CodeFieldProps extends CodeEditorProps {
  /** false → plain textarea, CodeMirror chunk is never loaded. */
  highlight?: boolean;
  rows?: number;
}

export default function CodeField({
  highlight = true,
  rows = 10,
  ...editor
}: CodeFieldProps) {
  const { value, onChange, editable = true, placeholder, invalid } = editor;

  // The textarea is both the highlight-off control and the Suspense
  // fallback. It is wired to the same value/onChange, so anything typed
  // before the editor finishes loading is preserved when CodeMirror mounts.
  const textarea = (
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      rows={rows}
      spellCheck={false}
      disabled={!editable}
      className={cn(textareaClass(!!invalid), 'font-mono text-xs')}
    />
  );

  if (!highlight) return textarea;

  return <Suspense fallback={textarea}>{<CodeEditor {...editor} />}</Suspense>;
}
